import React, { useEffect, useState } from "react";
import { scheduleAPI } from "../../../api/client";
import DayChipSelector from "../../../components/common/DayChipSelector";
import UserSchedulesTab from "../../../components/schedule/UserSchedulesTab";
import { formatDisplayDate, parseLocalDate } from "../../../utils/dateUtils";
import "../../../components/projects/Projects.css";

export default function OrgScheduleSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("org");
  const [form, setForm] = useState({
    workingDays: [],
    workStartTime: "09:00",
    workEndTime: "18:00",
    slaWarningDefault: 80,
  });
  const [holidays, setHolidays] = useState([]);
  const [newHoliday, setNewHoliday] = useState({ date: "", name: "" });
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const resp = await scheduleAPI.getOrg();
      const data = resp.data;
      setForm({
        workingDays: data.workingDays || [],
        workStartTime: data.workStartTime || "09:00",
        workEndTime: data.workEndTime || "18:00",
        slaWarningDefault: data.slaWarningDefault || 80,
      });
      setHolidays(data.publicHolidays || []);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load schedule");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await scheduleAPI.updateOrg(form);
      setMessage("Schedule settings saved.");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const addHoliday = async () => {
    if (!newHoliday.date || !newHoliday.name) return;
    setError("");
    try {
      const resp = await scheduleAPI.addHoliday(newHoliday);
      setHolidays((prev) => [...prev, resp.data]);
      setNewHoliday({ date: "", name: "" });
    } catch (err) {
      setError(err.response?.data?.message || "Failed to add holiday");
    }
  };

  const removeHoliday = async (id) => {
    setError("");
    try {
      await scheduleAPI.removeHoliday(id);
      setHolidays((prev) => prev.filter((h) => h.id !== id));
    } catch (err) {
      setError(err.response?.data?.message || "Failed to remove holiday");
    }
  };

  if (loading) return <div className="small-muted">Loading...</div>;

  return (
    <div className="project-detail-page">
      <div className="project-detail-header">
        <div>
          <h1>Schedule Settings</h1>
          <p className="small-muted">
            Configure default working days, hours, and holidays.
          </p>
        </div>
      </div>

      <div className="project-actions" style={{ marginBottom: 12 }}>
        <button
          className={activeTab === "org" ? "btn-primary" : "btn-secondary"}
          onClick={() => setActiveTab("org")}
        >
          Org Defaults
        </button>
        <button
          className={activeTab === "users" ? "btn-primary" : "btn-secondary"}
          onClick={() => setActiveTab("users")}
        >
          User Schedules
        </button>
      </div>

      {activeTab === "org" && (
        <>
          <div className="panel">
            <DayChipSelector
              selectedDays={form.workingDays}
              onChange={(days) =>
                setForm((prev) => ({ ...prev, workingDays: days }))
              }
              label="Which days does your team work?"
              helperText="This is the default for all users. You can set different days per person from their profile."
              mode="workdays"
            />
          </div>

          <div className="panel" style={{ marginTop: 12 }}>
            <h4>What are your standard working hours?</h4>
            <div className="modal-grid two-col">
              <input
                type="time"
                value={form.workStartTime}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    workStartTime: e.target.value,
                  }))
                }
              />
              <input
                type="time"
                value={form.workEndTime}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, workEndTime: e.target.value }))
                }
              />
            </div>
          </div>

          <div className="panel" style={{ marginTop: 12 }}>
            <h4>Days when the entire office is off</h4>
            <div className="stage-assign-row">
              <input
                type="date"
                value={newHoliday.date}
                onChange={(e) =>
                  setNewHoliday((prev) => ({
                    ...prev,
                    date: e.target.value,
                  }))
                }
              />
              <input
                type="text"
                placeholder="Holiday name"
                value={newHoliday.name}
                onChange={(e) =>
                  setNewHoliday((prev) => ({ ...prev, name: e.target.value }))
                }
              />
              <button className="btn-secondary" onClick={addHoliday}>
                + Add Holiday
              </button>
            </div>
            <div style={{ marginTop: 12 }}>
              {holidays.length === 0 && (
                <div className="small-muted">No holidays added yet.</div>
              )}
              {holidays.map((holiday) => (
                <div key={holiday.id} className="stage-assign-row">
                  <div>
                    {formatDisplayDate(parseLocalDate(holiday.date))} ·{" "}
                    {holiday.name}
                  </div>
                  <button
                    className="btn-secondary"
                    onClick={() => removeHoliday(holiday.id)}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* <div className="panel" style={{ marginTop: 12 }}>
            <h4>Warn me when a stage has used this much of its allocated time</h4>
            <input
              type="range"
              min="50"
              max="100"
              value={form.slaWarningDefault}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  slaWarningDefault: Number(e.target.value),
                }))
              }
            />
            <div className="small-muted">{form.slaWarningDefault}%</div>
          </div> */}

          <div style={{ marginTop: 12 }}>
            <button className="btn-primary" disabled={saving} onClick={save}>
              {saving ? "Saving..." : "Save All Settings"}
            </button>
          </div>

          {message && (
            <div className="small-muted" style={{ color: "#059669" }}>
              {message}
            </div>
          )}
          {error && (
            <div className="small-muted" style={{ color: "#b91c1c" }}>
              {error}
            </div>
          )}
        </>
      )}

      {activeTab === "users" && <UserSchedulesTab />}
    </div>
  );
}
